import type { PartnerResponse } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { Amount } from '@/components/amount';
import { cn } from '@/lib/utils';

import { balanceLines, type BalanceSource } from './partner-balance-lines';

/**
 * Wave 5B — the customer's BALANCE as a chip under the customer field of a
 * document editor: what they owe us (MD-01 field 29 / F-15, the server's
 * figure — opening balance + posted invoices − receipts, never typed) and the
 * credit bar the server already decided (BR-MD-01-09a/-09b: amber at 80% of
 * the limit, red past it while enforcement is on).
 *
 * The colour is the SERVER's verdict, drawn here and decided nowhere else —
 * the partner screen's own bar reads the same two fields. Nothing here
 * blocks: a red chip is information at the point of a sale, and the credit
 * refusal (if any) is the posting door's.
 *
 * Built for the delivery note first; any editor that holds a
 * `PartnerResponse` (`usePartnerPicker(...).byId`, `usePartnersHeld`) can
 * mount it under its customer field.
 *
 * ONE FIGURE PER CURRENCY (owner decision, 30 Oct 2026): a customer owing in
 * two currencies shows both amounts side by side, never a mixed sum. The tone
 * is still the one server verdict, which measures the converted exposure.
 */
export function PartnerBalanceChip({
  partner,
  className,
}: {
  partner: (BalanceSource & Pick<PartnerResponse, 'creditBar'>) | undefined;
  className?: string | undefined;
}) {
  const { t } = useTranslation();
  if (partner === undefined) return null;
  const tone =
    partner.creditBar === 'red'
      ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200'
      : partner.creditBar === 'amber'
        ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
        : 'border-border bg-muted/40 text-muted-foreground';
  return (
    <span
      role="status"
      aria-label={t('partners.balanceChipAria')}
      className={cn(
        'inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border px-2 py-1 text-xs',
        tone,
        className,
      )}
    >
      <span>{t('partners.balanceChip')}</span>
      {balanceLines(partner, 'customer').map((line) => (
        <Amount
          key={line.currency}
          value={line.value}
          currency={line.currency}
          className="font-semibold"
        />
      ))}
      {partner.creditBar === 'amber' ? <span>{t('partners.balanceNearLimit')}</span> : null}
      {partner.creditBar === 'red' ? <span>{t('partners.balanceOverLimit')}</span> : null}
    </span>
  );
}
